import type { CSSProperties, FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface MarqueeProps {
    children: ReactNode
    duration?: number
    reverse?: boolean
    fade?: boolean
    className?: string
    trackClassName?: string
    label?: string
}

// Endless horizontal loop. The content renders twice side by side and the track slides by half its width,
// so the seam is invisible. Hovering or focusing pauses it.
export const Marquee: FC<MarqueeProps> = ({
    children,
    duration = 60,
    reverse = false,
    fade = true,
    className,
    trackClassName,
    label
}): ReactNode => {
    const style = { '--fc-marquee-duration': `${duration}s` } as CSSProperties

    return (
        <div
            role={label ? 'region' : undefined}
            aria-label={label}
            className={cn('fc-marquee relative flex overflow-hidden', fade && 'fc-fade-x', className)}
            style={style}
        >
            <div className={cn('fc-marquee-track flex w-max shrink-0', reverse ? 'animate-marquee-reverse' : 'animate-marquee')}>
                <div className={cn('flex shrink-0 items-center', trackClassName)}>
                    {children}
                </div>

                <div aria-hidden='true' className={cn('flex shrink-0 items-center', trackClassName)}>
                    {children}
                </div>
            </div>
        </div>
    )
}
